import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Lock } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

import {
  trackInitiateCheckout,
  trackLead,
  trackMQL,
} from "@/lib/meta-pixel";
import { getTrackingParams } from "@/lib/tracking";
import { getNextSession } from "@/lib/selecao-sessions";

import {
  CARTEIRA_OPTIONS,
  selecaoFormSchema,
  type SelecaoFormData,
} from "./schema";

const CARTEIRA_VALUE_MAP: Record<string, number> = {
  ate1milhao: 1,
  "1a5milhoes": 5,
  "5a20milhoes": 20,
  "20a100milhoes": 100,
  acima100milhoes: 500,
};

const MQL_CARTEIRAS = new Set([
  "5a20milhoes",
  "20a100milhoes",
  "acima100milhoes",
]);

type FormState = Partial<SelecaoFormData>;
type FieldErrors = Partial<Record<keyof SelecaoFormData, string>>;

const inputBase =
  "h-12 sm:h-[52px] bg-white border-transparent text-[#112711] placeholder:text-zinc-400 focus:border-[#4BD084] focus-visible:ring-[#4BD084] focus-visible:ring-offset-[#112711] rounded-xl text-base";

export const SelecaoHeroForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const trackedFirstFocus = useRef(false);

  const [data, setData] = useState<FormState>({});
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const set = <K extends keyof SelecaoFormData>(
    key: K,
    value: SelecaoFormData[K]
  ) => {
    setData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const onFirstFocus = () => {
    if (trackedFirstFocus.current) return;
    trackInitiateCheckout({
      content_name: "Masterclass Allu — Programa de Parceiros",
      content_category: "selecao",
    });
    trackedFirstFocus.current = true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = selecaoFormSchema.safeParse(data);

    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      parsed.error.issues.forEach((issue) => {
        const key = issue.path[0] as keyof SelecaoFormData;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      });
      setErrors(fieldErrors);
      toast({
        title: "Confira os dados",
        description:
          parsed.error.issues[0]?.message ?? "Preencha todos os campos.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    // event_id compartilhado entre Pixel e CAPI server (dedup).
    const eventID =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const value = CARTEIRA_VALUE_MAP[parsed.data.carteira_size] ?? 1;
    const isMQL = MQL_CARTEIRAS.has(parsed.data.carteira_size);

    // Advanced Matching — fbq hasheia internamente.
    const [firstName, ...lastParts] = parsed.data.fullname.trim().split(/\s+/);
    const userData = {
      em: parsed.data.email,
      ph: parsed.data.phone.replace(/\D/g, ""),
      fn: firstName,
      ln: lastParts.join(" ") || undefined,
      country: "br",
    };

    // Dispara conversão no Pixel ANTES do await — evita race com navigate.
    trackLead(
      {
        content_name: "Masterclass Allu — Programa de Parceiros",
        content_category: "selecao",
        currency: "BRL",
        value,
        carteira_size: parsed.data.carteira_size,
        lead_quality: isMQL ? "mql" : "regular",
      },
      { eventID, userData }
    );

    if (isMQL) {
      trackMQL(
        {
          content_name: "Masterclass Allu — Programa de Parceiros",
          carteira_size: parsed.data.carteira_size,
          currency: "BRL",
          value,
        },
        { eventID: `${eventID}-mql`, userData }
      );
    }

    try {
      const tracking = getTrackingParams();
      // Recalcula a próxima sessão AGORA — evita data stale se a aba
      // ficou aberta horas/dias antes do submit.
      const nextSession = getNextSession();
      const res = await fetch("/api/selecao-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullname: parsed.data.fullname,
          email: parsed.data.email,
          phone: parsed.data.phone,
          carteira_size: parsed.data.carteira_size,
          event_id: eventID,
          session_iso: nextSession.iso,
          tracking,
        }),
      });
      if (!res.ok) throw new Error(`Submit failed (${res.status})`);
      navigate("/selecao/obrigado");
    } catch (err) {
      console.error("[Selecao] submit error:", err);
      toast({
        title: "Erro ao reservar",
        description:
          "Tente novamente em instantes. Se persistir, fale com a gente pelo e-mail.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      id="form"
      onSubmit={handleSubmit}
      noValidate
      className="bg-[#112711] rounded-3xl p-6 sm:p-7 w-full shadow-2xl shadow-[#112711]/25 ring-1 ring-[#1F4A33]/50"
    >
      <div className="mb-5">
        <h2 className="text-xl sm:text-2xl font-semibold text-white tracking-tight">
          Reservar minha vaga
        </h2>
        <p className="text-sm text-[#D1F8E2]/80 mt-1.5 leading-relaxed">
          Sessão ao vivo · sem replay · vagas verificadas por e-mail.
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="fullname" className="text-white text-sm font-medium">
            Nome completo
          </Label>
          <Input
            id="fullname"
            value={data.fullname ?? ""}
            onChange={(e) => set("fullname", e.target.value)}
            onFocus={onFirstFocus}
            placeholder="Seu nome e sobrenome"
            autoComplete="name"
            className={inputBase}
            aria-invalid={!!errors.fullname}
          />
          {errors.fullname && (
            <p className="text-xs text-red-600 mt-1">{errors.fullname}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-white text-sm font-medium">
            E-mail (onde você receberá o link de acesso)
          </Label>
          <Input
            id="email"
            type="email"
            value={data.email ?? ""}
            onChange={(e) => set("email", e.target.value)}
            onFocus={onFirstFocus}
            placeholder="voce@empresa.com.br"
            autoComplete="email"
            className={inputBase}
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <p className="text-xs text-red-600 mt-1">{errors.email}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="phone" className="text-white text-sm font-medium">
            WhatsApp (com DDD)
          </Label>
          <Input
            id="phone"
            type="tel"
            inputMode="tel"
            value={data.phone ?? ""}
            onChange={(e) => set("phone", formatPhone(e.target.value))}
            onFocus={onFirstFocus}
            placeholder="(11) 99999-9999"
            autoComplete="tel"
            className={inputBase}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && (
            <p className="text-xs text-red-600 mt-1">{errors.phone}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label
            htmlFor="carteira_size"
            className="text-white text-sm font-medium"
          >
            Volume de carteira sob assessoria (R$)
          </Label>
          <Select
            value={data.carteira_size ?? ""}
            onValueChange={(v) => set("carteira_size", v)}
          >
            <SelectTrigger
              id="carteira_size"
              className={inputBase}
              aria-invalid={!!errors.carteira_size}
              onFocus={onFirstFocus}
            >
              <SelectValue placeholder="Selecione uma faixa" />
            </SelectTrigger>
            <SelectContent>
              {CARTEIRA_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.carteira_size && (
            <p className="text-xs text-red-600 mt-1">{errors.carteira_size}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full mt-2 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-sm h-12 sm:h-[52px] bg-[#4BD084] hover:bg-[#5fdc94] text-[#112711] shadow-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[#112711] focus-visible:ring-[#4BD084] disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Reservando…
            </>
          ) : (
            <>
              Reservar minha vaga
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>

        <p className="text-[11px] text-[#D1F8E2]/60 text-center leading-relaxed pt-1 flex items-center justify-center gap-1.5">
          <Lock className="w-3 h-3" />
          Suas informações de carteira são tratadas em sigilo.
        </p>
      </div>
    </form>
  );
};
